import { useEffect, useState } from "react";
import { adminFetch } from "./adminApi";

const STATUSES = ["ALL", "OPEN", "UNDER_REVIEW", "RESOLVED", "DISMISSED"];
const STATUS_COLORS = {
  OPEN: "text-red-300 bg-red-500/15 border-red-400/30",
  UNDER_REVIEW: "text-yellow-300 bg-yellow-500/15 border-yellow-400/30",
  RESOLVED: "text-green-300 bg-green-500/15 border-green-400/30",
  DISMISSED: "text-white/50 bg-white/5 border-white/15",
};
const REASON_LABELS = {
  GHOSTED: "Didn't show up",
  NO_CONTACT: "Stopped responding",
  QUALITY: "Quality issue",
  PAYMENT: "Payment issue",
  OTHER: "Other",
};

function fmt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
}

export default function AdminDisputes() {
  const [statusFilter, setStatusFilter] = useState("OPEN");
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [selected, setSelected] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [adminNote, setAdminNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState("");

  const pageSize = 20;

  async function load() {
    setLoading(true);
    setErr("");
    try {
      const params = new URLSearchParams({ page, pageSize });
      if (statusFilter !== "ALL") params.set("status", statusFilter);
      const data = await adminFetch(`/api/admin/disputes?${params}`);
      setItems(Array.isArray(data?.items) ? data.items : []);
      setTotal(Number(data?.total || 0));
    } catch (e) {
      setErr(e?.message || "Failed to load disputes");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [statusFilter, page]); // eslint-disable-line

  function openDetail(item) {
    setSelected(item);
    setNewStatus(item.status);
    setAdminNote(item.adminNote || "");
    setSaveErr("");
  }

  async function save() {
    if (!selected) return;
    setSaving(true);
    setSaveErr("");
    try {
      await adminFetch(`/api/admin/disputes/${selected.id}`, {
        method: "PATCH",
        body: { status: newStatus, adminNote },
      });
      setSelected(null);
      load();
    } catch (e) {
      setSaveErr(e?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  const pages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold">Disputes</h2>
          <p className="text-sm text-white/50">{total} total</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => { setStatusFilter(s); setPage(1); }}
              className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition ${
                statusFilter === s
                  ? "border-[#7269e3]/60 bg-[#7269e3]/20 text-white"
                  : "border-white/10 bg-white/5 text-white/70 hover:bg-white/10"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {err && <div className="rounded-xl bg-red-500/10 p-3 text-sm text-red-200">{err}</div>}
      {loading && <div className="py-8 text-center text-white/50">Loading…</div>}

      {!loading && items.length === 0 && (
        <div className="rounded-2xl border border-dashed border-white/10 py-12 text-center text-white/50">
          No disputes found.
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="space-y-2">
          {items.map((d) => (
            <div
              key={d.id}
              className="cursor-pointer rounded-xl border border-white/10 bg-white/5 px-4 py-3 hover:bg-white/[0.08] transition"
              onClick={() => openDetail(d)}
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${STATUS_COLORS[d.status] || ""}`}>
                      {d.status}
                    </span>
                    <span className="text-sm font-semibold text-white/90">
                      {REASON_LABELS[d.reason] || d.reason}
                    </span>
                  </div>
                  <div className="mt-1 text-sm text-white/60">
                    {d.booking?.subject || "—"} · Student: {d.student?.email || "—"} → Tutor: {d.tutor?.email || "—"}
                  </div>
                  {d.description && (
                    <div className="mt-1 line-clamp-2 text-sm text-white/45">{d.description}</div>
                  )}
                </div>
                <div className="shrink-0 text-xs text-white/40">{fmt(d.createdAt)}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {pages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-sm disabled:opacity-40"
          >
            Previous
          </button>
          <span className="text-sm text-white/50">Page {page} / {pages}</span>
          <button
            onClick={() => setPage((p) => Math.min(pages, p + 1))}
            disabled={page === pages}
            className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-sm disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}

      {/* Detail panel */}
      {selected && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.65)" }}
          onClick={(e) => { if (e.target === e.currentTarget) setSelected(null); }}
        >
          <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#272c39] p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-lg">Dispute detail</h3>
              <button onClick={() => setSelected(null)} className="text-white/50 hover:text-white text-xl">×</button>
            </div>

            <div className="space-y-1 text-sm">
              <div><span className="text-white/50">Booking:</span> {selected.booking?.subject} · {fmt(selected.booking?.startISO)}</div>
              <div><span className="text-white/50">Student:</span> {selected.student?.name || "—"} ({selected.student?.email})</div>
              <div><span className="text-white/50">Tutor:</span> {selected.tutor?.name || "—"} ({selected.tutor?.email})</div>
              <div><span className="text-white/50">Reason:</span> {REASON_LABELS[selected.reason] || selected.reason}</div>
              <div><span className="text-white/50">Filed:</span> {fmt(selected.createdAt)}</div>
            </div>

            {selected.description && (
              <div className="rounded-xl bg-white/5 p-3 text-sm text-white/80 leading-relaxed">
                {selected.description}
              </div>
            )}

            <div>
              <label className="mb-1 block text-sm text-white/70">Status</label>
              <select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                className="w-full rounded-xl border border-white/10 bg-white/10 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#7269e3]"
              >
                {["OPEN", "UNDER_REVIEW", "RESOLVED", "DISMISSED"].map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-sm text-white/70">Admin note (internal)</label>
              <textarea
                rows={3}
                value={adminNote}
                onChange={(e) => setAdminNote(e.target.value)}
                maxLength={2000}
                className="w-full resize-y rounded-xl border border-white/10 bg-white/10 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#7269e3]"
              />
            </div>

            {saveErr && <div className="rounded-xl bg-red-500/10 p-3 text-sm text-red-200">{saveErr}</div>}

            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setSelected(null)}
                className="rounded-xl border border-white/10 px-4 py-2 text-sm hover:bg-white/5"
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="rounded-xl bg-[#7269e3] px-4 py-2 text-sm font-semibold hover:brightness-110 disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
